import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { fetchGptLogDetail, type GptLogDetailPayload } from "@/integrations/backend/gpt-logs";
import type { GptLogInputSnapshotRow } from "@/integrations/backend/program-generation";
import { toast } from "@/hooks/use-toast";

export function GptLogDetailDialog({
  open,
  onOpenChange,
  logId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  logId: string | null;
}) {
  const [data, setData] = useState<GptLogDetailPayload | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !logId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchGptLogDetail(logId)
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          toast({
            title: "Could not load GPT log",
            description: e instanceof Error ? e.message : "Request failed",
            variant: "destructive",
          });
          onOpenChange(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, logId, onOpenChange]);

  const messagesText =
    data?.messages !== undefined
      ? typeof data.messages === "string"
        ? data.messages
        : JSON.stringify(data.messages, null, 2)
      : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl flex flex-col gap-0 border-gray-200 bg-white p-0 text-gray-900 sm:max-w-4xl">
        <DialogHeader className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3 pr-12">
          <DialogTitle className="text-base font-semibold text-gray-950">
            GPT log {logId ? `${logId.slice(0, 8)}…` : ""}
          </DialogTitle>
          {data && (
            <p className="text-xs font-normal text-gray-700">
              {data.preset_label} · {data.type} · {data.model}
            </p>
          )}
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col divide-y divide-gray-200 overflow-hidden bg-white">
          <div className="min-h-0 flex-1 overflow-y-auto bg-white p-3">
            <div className="mb-1.5 text-xs font-semibold text-gray-800">
              Request messages (input)
            </div>
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-800">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-700" />
                Loading…
              </div>
            ) : (
              <pre className="max-h-[40vh] overflow-y-auto rounded-md border border-gray-300 bg-gray-100 p-3 text-xs leading-relaxed text-gray-950 whitespace-pre-wrap break-words">
                {messagesText || "—"}
              </pre>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto bg-white p-3">
            <div className="mb-1.5 text-xs font-semibold text-gray-800">Model output</div>
            {loading ? (
              <div className="py-4 text-sm text-gray-700">…</div>
            ) : (
              <>
                <pre className="mb-2 max-h-[32vh] overflow-y-auto rounded-md border border-gray-300 bg-gray-100 p-3 text-xs leading-relaxed text-gray-950 whitespace-pre-wrap break-words">
                  {data?.output ?? "—"}
                </pre>
                {data?.cleaned_output && data.cleaned_output !== data.output && (
                  <>
                    <div className="mb-1.5 text-xs font-semibold text-gray-800">Cleaned output</div>
                    <pre className="max-h-[24vh] overflow-y-auto rounded-md border border-gray-300 bg-gray-100 p-3 text-xs leading-relaxed text-gray-950 whitespace-pre-wrap break-words">
                      {data.cleaned_output}
                    </pre>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-3">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function GptLogInputsDialog({
  open,
  onOpenChange,
  logId,
  rows,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  logId: string | null;
  rows: GptLogInputSnapshotRow[] | undefined;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-lg flex flex-col gap-0 border-gray-200 bg-white p-0 text-gray-900">
        <DialogHeader className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3 pr-12">
          <DialogTitle className="text-base font-semibold text-gray-950">
            Prompt inputs {logId ? `(${logId.slice(0, 8)}…)` : ""}
          </DialogTitle>
          <p className="text-xs font-normal text-gray-700">
            Fields we consider part of this call's context (not read from the raw log).
          </p>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto bg-white p-3">
          {!rows || rows.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-800">No snapshot for this call.</p>
          ) : (
            <div className="overflow-hidden rounded-md border border-gray-300 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-300 bg-gray-200 text-left">
                    <th className="w-[36%] px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-900">
                      Input
                    </th>
                    <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-900">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.map((row, i) => (
                    <tr key={i} className="align-top odd:bg-white even:bg-gray-50">
                      <td className="border-r border-gray-200 px-3 py-2.5 font-semibold text-gray-950">
                        {row.label}
                      </td>
                      <td className="px-3 py-2.5 whitespace-pre-wrap break-words text-gray-900">
                        {row.value || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <DialogFooter className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-3">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
